// Iter 62at — 2-screen navigable client view (list → detail)
//   Screen 1: cards des clients avec avatar/photo + count groupes
//   Screen 2: liste des 3 groupes du client + bouton "+ Créer un groupe"
// Founder-only tools: upload de l'avatar client + création de nouveau groupe.
import { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ChevronRight, ChevronLeft, MessageCircle, Camera, Plus, X, Users } from './icons';

export function ClientTreeView({ onOpenConversation, isMobile = false }) {
  const { isFounder } = useAuth();
  const [tree, setTree] = useState({ clients: [] });
  const [loading, setLoading] = useState(true);
  const [openClient, setOpenClient] = useState(null); // client id
  const [createFor, setCreateFor] = useState(null);   // client obj for new-group modal

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/conversations/tree');
      setTree(r.data || { clients: [] });
    } catch (e) {
      setTree({ clients: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const h = () => load();
    window.addEventListener('asa-messages-refresh', h);
    return () => window.removeEventListener('asa-messages-refresh', h);
  }, [load]);

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="shimmer" style={{ height: 72, borderRadius: 14 }} />
        ))}
      </div>
    );
  }

  const clients = tree.clients || [];
  if (clients.length === 0) {
    return (
      <div style={{
        padding: '40px 24px', textAlign: 'center',
        border: '1px dashed var(--bd)', borderRadius: 14,
        color: 'var(--t3)', fontFamily: 'Sora', fontSize: 13,
      }}>
        <Users size={26} color="var(--t4)" strokeWidth={1.4} />
        <div style={{ marginTop: 10, fontWeight: 600 }}>Aucun client pour l&apos;instant</div>
        <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--t4)' }}>
          Crée un client depuis le Hub Clients pour commencer
        </div>
      </div>
    );
  }

  // Screen 2 — Detail for one client
  if (openClient) {
    const cli = clients.find((c) => (c.client?.id || c.id) === openClient);
    if (!cli) { setOpenClient(null); return null; }
    return (
      <>
        <ClientDetailView
          cli={cli}
          isFounder={isFounder}
          onBack={() => setOpenClient(null)}
          onOpenConv={onOpenConversation}
          onNewGroup={() => setCreateFor(cli.client)}
          onAvatarChanged={load}
          isMobile={isMobile}
        />
        {createFor && (
          <CreateGroupModal
            client={createFor}
            onClose={() => setCreateFor(null)}
            onCreated={(conv) => {
              setCreateFor(null);
              load();
              if (onOpenConversation) onOpenConversation(conv);
            }}
          />
        )}
      </>
    );
  }

  // Screen 1 — List of clients
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {clients.map((cli, idx) => (
        <ClientCard
          key={cli.client?.id || `no-${idx}`}
          cli={cli}
          onTap={() => setOpenClient(cli.client?.id || cli.id)}
        />
      ))}
    </div>
  );
}

function ClientCard({ cli, onTap }) {
  const client = cli.client || {};
  const clientName = client.name || 'Sans client';
  const accent = client.color || '#5BBEFC';
  const initials = clientName.split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('') || '?';
  const convs = cli.conversations || [];
  const unread = cli.unread || 0;

  return (
    <button
      data-testid={`tree-client-${client.id || 'none'}`}
      onClick={onTap}
      style={{
        width: '100%',
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '14px 16px',
        borderRadius: 14,
        background: 'var(--sf)',
        border: `1px solid ${accent}33`,
        boxShadow: `inset 0 1px 0 rgba(255,255,255,0.04), 0 3px 10px ${accent}14`,
        cursor: 'pointer', textAlign: 'left',
        transition: 'all 0.15s var(--ease-out)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = `${accent}66`;
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = `${accent}33`;
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <ClientAvatar client={client} accent={accent} initials={initials} size={40} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'Sora', fontWeight: 800, fontSize: 15,
          color: 'var(--t1)', letterSpacing: '-0.01em',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{clientName}</div>
        <div style={{
          fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
          color: 'var(--t3)', marginTop: 2,
          textTransform: 'uppercase', letterSpacing: '0.08em',
        }}>{convs.length} groupe{convs.length > 1 ? 's' : ''}</div>
      </div>
      {unread > 0 && (
        <span style={{
          padding: '2px 8px', borderRadius: 999,
          background: '#FF6B6B', color: '#fff',
          fontFamily: 'Sora', fontWeight: 800, fontSize: 10,
        }}>{unread}</span>
      )}
      <ChevronRight size={14} color="var(--t3)" strokeWidth={2.2} />
    </button>
  );
}

function ClientAvatar({ client, accent, initials, size = 40, editable = false, onChange }) {
  const inputRef = useRef(null);
  const src = client.avatar;
  const dim = size;
  const inner = (
    <span
      aria-hidden
      style={{
        width: dim, height: dim, borderRadius: dim / 3,
        display: 'grid', placeItems: 'center',
        background: src
          ? `url(${src}) center / cover no-repeat`
          : `linear-gradient(140deg, ${accent} 0%, rgba(255,255,255,0.15) 50%, ${accent} 100%)`,
        color: '#0B1220',
        fontFamily: 'Sora', fontWeight: 800, fontSize: dim / 3.2,
        boxShadow: `inset 0 1px 0 rgba(255,255,255,0.5), 0 3px 8px ${accent}44`,
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      {!src && initials}
    </span>
  );
  if (!editable) return inner;
  return (
    <>
      <div
        style={{ position: 'relative', width: dim, height: dim, cursor: 'pointer' }}
        onClick={() => inputRef.current?.click()}
        data-testid="client-avatar-edit"
      >
        {inner}
        <span style={{
          position: 'absolute', bottom: -3, right: -3,
          width: 18, height: 18, borderRadius: '50%',
          background: 'var(--sf2)', border: `1.5px solid ${accent}`,
          display: 'grid', placeItems: 'center',
          color: accent,
        }}>
          <Camera size={9} strokeWidth={2.4} />
        </span>
      </div>
      <input
        ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={async (e) => {
          const f = e.target.files?.[0]; if (!f) return;
          if (f.size > 800 * 1024) { toast.error('Image trop lourde (max 800 Ko)'); return; }
          const reader = new FileReader();
          reader.onload = () => onChange?.(reader.result);
          reader.readAsDataURL(f);
        }}
      />
    </>
  );
}

function ClientDetailView({ cli, isFounder, onBack, onOpenConv, onNewGroup, onAvatarChanged, isMobile }) {
  const client = cli.client || {};
  const clientName = client.name || 'Sans client';
  const accent = client.color || '#5BBEFC';
  const initials = clientName.split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('') || '?';
  const convs = cli.conversations || [];

  const uploadAvatar = async (dataUrl) => {
    try {
      await api.patch(`/clients/${client.id}`, { avatar: dataUrl });
      toast.success('Photo mise à jour');
      onAvatarChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur upload');
    }
  };

  return (
    <div data-testid={`tree-client-detail-${client.id}`}>
      {/* Back header */}
      <button
        onClick={onBack}
        data-testid="tree-client-back"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'transparent', border: 'none',
          color: 'var(--t2)', fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
          padding: '4px 8px 12px 0', cursor: 'pointer',
        }}
      >
        <ChevronLeft size={14} /> Retour aux clients
      </button>

      {/* Client hero */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '16px 18px',
        borderRadius: 16,
        background: `linear-gradient(140deg, ${accent}14 0%, transparent 60%), var(--sf)`,
        border: `1px solid ${accent}44`,
        marginBottom: 14,
      }}>
        <ClientAvatar
          client={client} accent={accent} initials={initials}
          size={54}
          editable={isFounder}
          onChange={uploadAvatar}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: 'Sora', fontWeight: 900, fontSize: 20,
            color: 'var(--t1)', letterSpacing: '-0.01em',
          }}>{clientName}</div>
          <div style={{
            fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
            color: accent, marginTop: 2,
            textTransform: 'uppercase', letterSpacing: '0.10em',
          }}>{convs.length} groupe{convs.length > 1 ? 's' : ''}</div>
        </div>
      </div>

      {/* Groups list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {convs.length === 0 && (
          <div style={{
            padding: '30px 20px', textAlign: 'center',
            border: '1px dashed var(--bd)', borderRadius: 14,
            color: 'var(--t3)', fontFamily: 'Sora', fontSize: 13,
          }}>
            Aucun groupe pour ce client.
          </div>
        )}
        {convs.map((conv) => (
          <GroupRow key={conv.id} conv={conv} accent={accent} onClick={() => onOpenConv?.(conv)} isMobile={isMobile} />
        ))}
        {isFounder && (
          <button
            data-testid={`tree-client-add-group-${client.id}`}
            onClick={onNewGroup}
            style={{
              marginTop: 4,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '11px 12px', borderRadius: 10,
              background: 'transparent',
              border: `1.5px dashed ${accent}88`,
              color: accent,
              fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
              cursor: 'pointer', transition: 'all 0.12s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = `${accent}12`; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <Plus size={12} strokeWidth={2.4} /> Créer un groupe client
          </button>
        )}
      </div>
    </div>
  );
}

function GroupRow({ conv, accent, onClick, isMobile }) {
  const last = conv.last;
  const preview = last?.deleted
    ? '· Message supprimé ·'
    : (last?.text || '').replace(/\s+/g, ' ').trim().slice(0, 70);
  return (
    <button
      data-testid={`tree-conv-${conv.id}`}
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex', alignItems: 'center', gap: 12,
        padding: isMobile ? '12px 14px' : '11px 14px',
        borderRadius: 12,
        background: 'var(--sf)',
        border: '1px solid var(--bd)',
        color: 'var(--t1)',
        textAlign: 'left', cursor: 'pointer',
        transition: 'all 0.12s var(--ease-out)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${accent}55`; e.currentTarget.style.background = 'var(--sf2)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--bd)'; e.currentTarget.style.background = 'var(--sf)'; }}
    >
      <span aria-hidden style={{
        width: 32, height: 32, borderRadius: 10,
        display: 'grid', placeItems: 'center',
        background: `${accent}1F`, border: `1px solid ${accent}55`,
        color: accent, flexShrink: 0,
      }}>
        <MessageCircle size={14} strokeWidth={1.8} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'Sora', fontWeight: 800, fontSize: 13.5,
          color: 'var(--t1)', letterSpacing: '-0.01em',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{conv.name}</div>
        {preview ? (
          <div style={{
            fontFamily: 'Sora', fontWeight: 500, fontSize: 11,
            color: 'var(--t3)', marginTop: 2,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{preview}</div>
        ) : (
          <div style={{
            fontFamily: 'Sora', fontWeight: 500, fontSize: 11,
            color: 'var(--t4)', marginTop: 2, fontStyle: 'italic',
          }}>{conv.member_count || 0} membre(s) · aucun message</div>
        )}
      </div>
      {conv.unread > 0 && (
        <span style={{
          padding: '2px 8px', borderRadius: 999,
          background: '#FF6B6B', color: '#fff',
          fontFamily: 'Sora', fontWeight: 800, fontSize: 10,
        }}>{conv.unread}</span>
      )}
      <ChevronRight size={13} color="var(--t3)" />
    </button>
  );
}

// Iter 62aq/at — Founder quick create-group modal (used from the detail view)
function CreateGroupModal({ client, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState({});
  const [saving, setSaving] = useState(false);
  const accent = client?.color || '#5BBEFC';

  useEffect(() => {
    api.get('/users').then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);

  const suggestions = [
    { label: 'Montage vidéo',   color: '#5BBEFC' },
    { label: 'Head of Content', color: '#B58BFF' },
    { label: 'Stratégie',       color: '#F5C56A' },
  ];

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error('Nom obligatoire'); return; }
    const members = Object.keys(selected).filter((k) => selected[k]);
    setSaving(true);
    try {
      const r = await api.post('/conversations/group', {
        name: trimmed,
        members,
        client_id: client.id,
        color: accent,
      });
      toast.success(`Groupe "${trimmed}" créé`);
      onCreated?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(3,7,18,0.72)', backdropFilter: 'blur(8px)',
        display: 'grid', placeItems: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="create-group-modal"
        style={{
          width: '100%', maxWidth: 440,
          background: 'var(--sf)',
          borderRadius: 18,
          border: `1px solid ${accent}55`,
          boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
          overflow: 'hidden',
        }}
      >
        <div style={{
          padding: '16px 20px',
          background: `linear-gradient(140deg, ${accent}22 0%, transparent 100%)`,
          borderBottom: `1px solid ${accent}33`,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{
              fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5,
              color: accent, letterSpacing: '0.14em', textTransform: 'uppercase',
            }}>Nouveau groupe · {client.name}</div>
            <div style={{
              fontFamily: 'Sora', fontWeight: 800, fontSize: 16,
              color: 'var(--t1)', marginTop: 2,
            }}>Créer un groupe</div>
          </div>
          <button onClick={onClose} data-testid="create-group-close" style={{
            width: 30, height: 30, borderRadius: 8,
            background: 'var(--sf2)', border: '1px solid var(--bd)',
            color: 'var(--t2)', cursor: 'pointer',
            display: 'grid', placeItems: 'center',
          }}><X size={13} /></button>
        </div>

        <div style={{ padding: '16px 20px' }}>
          <label style={{
            fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
            color: 'var(--t3)', letterSpacing: '0.12em', textTransform: 'uppercase',
          }}>Nom du groupe</label>
          <input
            data-testid="create-group-name" autoFocus value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={`ex. ${client.name} Montage vidéo`}
            style={{
              width: '100%', marginTop: 6, padding: '10px 12px',
              borderRadius: 10, border: '1px solid var(--bd)',
              background: 'var(--sf2)', color: 'var(--t1)',
              fontFamily: 'Sora', fontWeight: 600, fontSize: 13, outline: 'none',
            }}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            {suggestions.map((s) => (
              <button
                key={s.label}
                data-testid={`create-group-suggest-${s.label}`}
                onClick={() => setName(`${client.name} ${s.label}`)}
                style={{
                  padding: '5px 10px', borderRadius: 999,
                  background: `${s.color}18`, color: s.color,
                  border: `1px solid ${s.color}44`,
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
                  cursor: 'pointer',
                }}
              >{client.name} {s.label}</button>
            ))}
          </div>

          <label style={{
            display: 'block', marginTop: 16,
            fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
            color: 'var(--t3)', letterSpacing: '0.12em', textTransform: 'uppercase',
          }}>Membres ({Object.values(selected).filter(Boolean).length})</label>
          <div style={{
            marginTop: 8, maxHeight: 220, overflowY: 'auto',
            display: 'flex', flexDirection: 'column', gap: 4,
            padding: 4, borderRadius: 10, border: '1px solid var(--bd)', background: 'var(--sf2)',
          }}>
            {users.map((u) => {
              const isSel = !!selected[u.id];
              return (
                <button
                  key={u.id}
                  data-testid={`create-group-member-${u.id}`}
                  onClick={() => setSelected((s) => ({ ...s, [u.id]: !s[u.id] }))}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '7px 8px', borderRadius: 8,
                    background: isSel ? `${accent}22` : 'transparent',
                    border: `1px solid ${isSel ? accent : 'transparent'}`,
                    color: 'var(--t1)', textAlign: 'left', cursor: 'pointer',
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
                  }}
                >
                  <span style={{
                    width: 22, height: 22, borderRadius: 999,
                    display: 'grid', placeItems: 'center',
                    background: u.color || '#5BBEFC', color: '#0B1220',
                    fontFamily: 'Sora', fontWeight: 800, fontSize: 10,
                    flexShrink: 0,
                  }}>{u.short || u.name?.[0] || '?'}</span>
                  <span style={{ flex: 1 }}>{u.name}</span>
                  {isSel && <span style={{ color: accent, fontSize: 11, fontWeight: 800 }}>✓</span>}
                </button>
              );
            })}
          </div>

          <button
            onClick={submit}
            disabled={saving || !name.trim()}
            data-testid="create-group-submit"
            style={{
              width: '100%', marginTop: 16,
              padding: '12px', borderRadius: 12,
              background: name.trim()
                ? `linear-gradient(140deg, ${accent} 0%, ${accent}CC 100%)`
                : 'var(--sf2)',
              border: '1px solid ' + (name.trim() ? accent : 'var(--bd)'),
              color: name.trim() ? '#0B1220' : 'var(--t3)',
              fontFamily: 'Sora', fontWeight: 800, fontSize: 13,
              cursor: name.trim() && !saving ? 'pointer' : 'not-allowed',
              opacity: saving ? 0.6 : 1,
            }}
          >{saving ? 'Création…' : 'Créer le groupe'}</button>
        </div>
      </div>
    </div>
  );
}
